import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';

import { QuantitySelector } from '@src/stories/elements/QuantitySelector/QuantitySelector';

import helpers from 'frontend/js/helpers';

import useCurrentTextStyle from '@src/hooks/useCurrentTextStyle';
import useMediaQuery from '@src/hooks/useMediaQuery';

import { ICartItem } from '@src/types/cart';
import { Collapsible } from '@src/stories/elements/Collapsible/Collapsible';
import useCurrency from '@src/hooks/useCurrency';

interface CartItemProps {
  item: ICartItem;
  settings: any;
  index: number;
  type: 'minicart' | 'cart';
  onQuantityChange?: (item: ICartItem, newQuantity: number) => void;
  onRemoveItem?: (item: ICartItem) => void;
  disableUndoPopup?: boolean; // New prop to disable the undo/wishlist popup
  isFirstItem?: boolean;
  isLastItem?: boolean;
}

interface CartItemSettings {
  show_vendor?: boolean;
  show_price?: boolean;
  show_product_options?: boolean;
  show_line_discounts?: boolean;

  vendor_text_style?: string;
  vendor_text_style_mobile?: string;
  vendor_text_style_tablet?: string;
  vendor_text_style_ultrawide?: string;
  vendor_text_style_enable_responsive?: boolean;
  vendor_text_color?: string;
  product_title_style?: string;
  product_title_style_mobile?: string;
  product_title_style_tablet?: string;
  product_title_style_ultrawide?: string;
  product_title_style_enable_responsive?: boolean;
  product_title_text_color?: string;
  compare_price_style?: string;
  compare_price_style_mobile?: string;
  compare_price_style_tablet?: string;
  compare_price_style_ultrawide?: string;
  compare_price_style_enable_responsive?: boolean;
  compare_price_text_color?: string;
  final_price_style?: string;
  final_price_style_mobile?: string;
  final_price_style_tablet?: string;
  final_price_style_ultrawide?: string;
  final_price_style_enable_responsive?: boolean;
  final_price_text_color?: string;
  product_option_style?: string;
  product_option_style_mobile?: string;
  product_option_style_tablet?: string;
  product_option_style_ultrawide?: string;
  product_option_style_enable_responsive?: boolean;
  product_option_text_color?: string;
  discount_text_style?: string;
  discount_text_style_mobile?: string;
  discount_text_style_tablet?: string;
  discount_text_style_ultrawide?: string;
  discount_text_style_enable_responsive?: boolean;
  discount_text_color?: string;

  // Item divider settings
  show_item_divider?: boolean;
  item_divider_color?: string;
  item_padding_top?: number;
  item_padding_bottom?: number;
  item_padding_left?: number;
  item_padding_right?: number;
  item_padding_top_mobile?: number;
  item_padding_bottom_mobile?: number;
  item_padding_left_mobile?: number;
  item_padding_right_mobile?: number;
  item_margin_top?: number;
  item_margin_bottom?: number;
  item_margin_left?: number;
  item_margin_right?: number;
  item_margin_top_mobile?: number;
  item_margin_bottom_mobile?: number;
  item_margin_left_mobile?: number;
  item_margin_right_mobile?: number;
}

export const CartItem = React.memo(
  ({
    item,
    settings,
    index,
    type = 'minicart',
    onQuantityChange,
    onRemoveItem,
    disableUndoPopup = false,
    isFirstItem = false,
    isLastItem = false
  }: CartItemProps) => {
    const { getCurrentTextStyle } = useCurrentTextStyle();
    const isMobile = useMediaQuery(null, 767);
    const staticCurrencySymbol = (window as any).store_currency_symbol || '$';
    const { currentCurrencySymbol } = useCurrency();
    const currencySymbolDisplay = staticCurrencySymbol || currentCurrencySymbol;

    // Memoize settings extraction
    const itemSettings: CartItemSettings = useMemo(
      () => ({
        show_vendor: settings.show_vendor ?? true,
        show_price: settings.show_price ?? true,
        show_line_discounts: settings.show_line_discounts ?? true,
        show_product_options: settings.show_product_options ?? true,

        vendor_text_style: settings.vendor_text_style,
        vendor_text_style_mobile: settings.vendor_text_style_mobile,
        vendor_text_style_tablet: settings.vendor_text_style_tablet,
        vendor_text_style_ultrawide: settings.vendor_text_style_ultrawide,
        vendor_text_style_enable_responsive: settings.vendor_text_style_enable_responsive,
        vendor_text_color: settings.vendor_text_color,
        product_title_style: settings.product_title_style,
        product_title_style_mobile: settings.product_title_style_mobile,
        product_title_style_tablet: settings.product_title_style_tablet,
        product_title_style_ultrawide: settings.product_title_style_ultrawide,
        product_title_style_enable_responsive: settings.product_title_style_enable_responsive,
        product_title_text_color: settings.product_title_text_color,
        compare_price_style: settings.compare_price_style,
        compare_price_style_mobile: settings.compare_price_style_mobile,
        compare_price_style_tablet: settings.compare_price_style_tablet,
        compare_price_style_ultrawide: settings.compare_price_style_ultrawide,
        compare_price_style_enable_responsive: settings.compare_price_style_enable_responsive,
        compare_price_text_color: settings.compare_price_text_color,
        final_price_style: settings.final_price_style,
        final_price_style_mobile: settings.final_price_style_mobile,
        final_price_style_tablet: settings.final_price_style_tablet,
        final_price_style_ultrawide: settings.final_price_style_ultrawide,
        final_price_style_enable_responsive: settings.final_price_style_enable_responsive,
        final_price_text_color: settings.final_price_text_color,
        product_option_style: settings.product_option_style,
        product_option_style_mobile: settings.product_option_style_mobile,
        product_option_style_tablet: settings.product_option_style_tablet,
        product_option_style_ultrawide: settings.product_option_style_ultrawide,
        product_option_style_enable_responsive: settings.product_option_style_enable_responsive,
        product_option_text_color: settings.product_option_text_color,
        discount_text_style: settings.discount_text_style,
        discount_text_style_mobile: settings.discount_text_style_mobile,
        discount_text_style_tablet: settings.discount_text_style_tablet,
        discount_text_style_ultrawide: settings.discount_text_style_ultrawide,
        discount_text_style_enable_responsive: settings.discount_text_style_enable_responsive,
        discount_text_color: settings.discount_text_color,

        show_item_divider: isLastItem
          ? false
          : isMobile
          ? settings.show_item_divider_mobile
          : settings.show_item_divider,
        item_divider_color: settings.item_divider_color ?? '#e5e7eb',
        item_padding_top:
          isFirstItem && type === 'minicart'
            ? 0
            : isMobile
            ? settings.item_padding_top_mobile
            : settings.item_padding_top ?? 0,
        item_padding_bottom: isMobile ? settings.item_padding_bottom_mobile : settings.item_padding_bottom ?? 0,
        item_padding_left: isMobile ? settings.item_padding_left_mobile : settings.item_padding_left ?? 0,
        item_padding_right: isMobile ? settings.item_padding_right_mobile : settings.item_padding_right ?? 0,
        item_margin_top: isMobile ? settings.item_margin_top_mobile : settings.item_margin_top ?? 0,
        item_margin_bottom: isMobile ? settings.item_margin_bottom_mobile : settings.item_margin_bottom ?? 0,
        item_margin_left: isMobile ? settings.item_margin_left_mobile : settings.item_margin_left ?? 0,
        item_margin_right: isMobile ? settings.item_margin_right_mobile : settings.item_margin_right ?? 0
      }),
      [settings, isMobile, isFirstItem]
    );

    // Find fashion_colour value from tags
    const fashionColourTag = item.tags?.find((tag) => tag.includes('fashion_colour:'));
    const fashionColourValue = fashionColourTag ? fashionColourTag.split('fashion_colour:')[1] : null;

    const cartType = type || 'minicart';

    const itemStyle = useMemo(
      () => ({
        borderBottom: itemSettings.show_item_divider ? `1px solid ${itemSettings.item_divider_color}` : 'none',
        paddingBottom: `${itemSettings.item_padding_bottom}px`,
        paddingTop: `${itemSettings.item_padding_top}px`,
        paddingLeft: `${itemSettings.item_padding_left}px`,
        paddingRight: `${itemSettings.item_padding_right}px`,
        marginBottom: `${itemSettings.item_margin_bottom}px`,
        marginTop: `${itemSettings.item_margin_top}px`,
        marginLeft: `${itemSettings.item_margin_left}px`,
        marginRight: `${itemSettings.item_margin_right}px`
      }),
      [itemSettings]
    );

    // Memoize utility functions
    const formatMoney = useCallback(
      (cents: number) => {
        if (!cents) return null;

        return `${currencySymbolDisplay}${(cents / 100).toFixed(2)}`;
      },
      [currencySymbolDisplay]
    );

    const handleQuantityChange = async (newQuantity: number) => {
      if (!onQuantityChange) return;

      onQuantityChange(item, newQuantity);
    };

    const handleRemoveItem = async () => {
      if (!onRemoveItem) return;
      onRemoveItem(item);
    };

    const filteredProductOptions = useMemo(() => {
      const productOptions = item?.product_options || {};
      // Ensure Object.entries never receives null/undefined
      return Object.entries(productOptions).filter(([key, value]) => value != null && value !== '');
    }, [item?.product_options]);

    return (
      <li
        className={`minicart-items__item`}
        style={{
          ...itemStyle
        }}
      >
        <div className='flex items-stretch'>
          <div
            className={`minicart-items__item-image mr-3 flex-shrink-0  min-w-20 ${
              cartType === 'minicart' ? 'basis-[27%]' : 'basis-[12%]'
            }`}
          >
            <a
              href={item.url}
              aria-label={`View ${item.product_title}`}
            >
              <img
                src={item.image}
                alt={item.product_title}
              />
            </a>
          </div>

          <div className='minicart-items__item-content flex-grow flex flex-col'>
            <div className='flex justify-between gap-1'>
              <div>
                {itemSettings.show_vendor && item.vendor && (
                  <div
                    className={`vendor mb-1 ${getCurrentTextStyle(
                      itemSettings.vendor_text_style,
                      itemSettings.vendor_text_style_enable_responsive,
                      itemSettings.vendor_text_style_mobile,
                      itemSettings.vendor_text_style_tablet,
                      itemSettings.vendor_text_style_ultrawide
                    )}`}
                    style={{ color: itemSettings.vendor_text_color }}
                  >
                    {item.vendor}
                  </div>
                )}

                <a
                  href={item.url}
                  aria-label={`View ${item.product_title}`}
                >
                  <h3
                    className={getCurrentTextStyle(
                      itemSettings.product_title_style,
                      itemSettings.product_title_style_enable_responsive,
                      itemSettings.product_title_style_mobile,
                      itemSettings.product_title_style_tablet,
                      itemSettings.product_title_style_ultrawide
                    )}
                    style={{ color: itemSettings.product_title_text_color }}
                  >
                    {item.product_title}
                  </h3>
                </a>
              </div>

              {itemSettings.show_price && (
                <div
                  className={`line-item-prices flex ${
                    cartType === 'minicart' ? 'flex-col' : 'max-lg:flex-col lg:items-center lg:gap-2'
                  } items-end gap-1`}
                >
                  {item.compare_at_price && (
                    <s
                      className={`price ${getCurrentTextStyle(
                        itemSettings.compare_price_style,
                        itemSettings.compare_price_style_enable_responsive,
                        itemSettings.compare_price_style_mobile,
                        itemSettings.compare_price_style_tablet,
                        itemSettings.compare_price_style_ultrawide
                      )}`}
                      style={{ color: itemSettings.compare_price_text_color }}
                    >
                      {formatMoney(item.compare_at_price)}
                    </s>
                  )}
                  {item.final_line_price > 0 && (
                    <div
                      className={`price ${getCurrentTextStyle(
                        itemSettings.final_price_style,
                        itemSettings.final_price_style_enable_responsive,
                        itemSettings.final_price_style_mobile,
                        itemSettings.final_price_style_tablet,
                        itemSettings.final_price_style_ultrawide
                      )}`}
                      style={{ color: itemSettings.final_price_text_color }}
                    >
                      {formatMoney(item.final_price)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {itemSettings.show_product_options && filteredProductOptions && filteredProductOptions.length > 0 && (
              <div className={`space-y-1 ${cartType === 'minicart' ? 'mt-2' : 'mt-6'}`}>
                {filteredProductOptions.map(([key, value], index) => {
                  const isColourOption = helpers.capitalizeFirst(key) === 'Colour';
                  return (
                    <div
                      key={index}
                      className={`product-option ${getCurrentTextStyle(
                        itemSettings.product_option_style,
                        itemSettings.product_option_style_enable_responsive,
                        itemSettings.product_option_style_mobile,
                        itemSettings.product_option_style_tablet,
                        itemSettings.product_option_style_ultrawide
                      )}`}
                      style={{ color: itemSettings.product_option_text_color }}
                    >
                      {helpers.capitalizeFirst(key)}:{' '}
                      {isColourOption && fashionColourValue ? fashionColourValue : value}
                    </div>
                  );
                })}
              </div>
            )}

            {itemSettings.show_line_discounts && item.discount_allocations && item.discount_allocations.length > 0 && (
              <div
                className={`line-discounts mt-3 lg:mt-6 ${getCurrentTextStyle(
                  itemSettings.discount_text_style,
                  itemSettings.discount_text_style_enable_responsive,
                  itemSettings.discount_text_style_mobile,
                  itemSettings.discount_text_style_tablet,
                  itemSettings.discount_text_style_ultrawide
                )}`}
                style={{ color: itemSettings.discount_text_color }}
              >
                {item.discount_allocations.length > 1 ? (
                  <Collapsible
                    title={`${item.discount_allocations.length} discounts have been applied`}
                    titleClass='b7--bold'
                    children={undefined}
                  >
                    <div className='flex flex-col gap-2 pb-2'>
                      {item.discount_allocations.map((discount, discountIndex) => (
                        <div
                          key={discountIndex}
                          className='line-discount-item flex items-center justify-between'
                        >
                          <span>{discount.title}</span>
                          <span>-{formatMoney(discount.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </Collapsible>
                ) : (
                  <div className='flex flex-col gap-2'>
                    <div className='line-discount-item flex items-center justify-between'>
                      <span>{item.discount_allocations[0].title} applied</span>
                      <span>-{formatMoney(item.discount_allocations[0].amount)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className='discounts-applied flex pt-2 lg:pt-4 justify-between mt-auto'>
              <QuantitySelector
                quantity={item.quantity}
                onQuantityChange={handleQuantityChange}
                minQuantity={1}
              />

              <div
                className={`remove-item-container flex items-end ${cartType === 'minicart' ? '' : 'lg:items-center'}`}
              >
                <button
                  className='remove-item-btn hover:opacity-50 transition-opacity'
                  onClick={handleRemoveItem}
                  aria-label='Remove item'
                >
                  <i
                    className={`icon icon-bin text-[18px] ${
                      cartType === 'minicart' ? 'lg:text-[16px]' : 'lg:text-[18px]'
                    }`}
                  >
                    {' '}
                  </i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </li>
    );
  }
);
